import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  ContentCopy,
  Share,
} from "@mui/icons-material";
import Partner from "modules/parties/types/Partner";
import { ROUTE_TEMPLATE } from "modules/navigation/routes";

type Props = { partner: Partner };

const MedicaidEmbedInstructions: React.FC<Props> = ({ partner }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const embedUrl = `${window.location.origin}${ROUTE_TEMPLATE.PUBLIC_MEDICAID_EMBED}?partnerId=${partner.id}`;

  const iframeSnippet =
    `<iframe\n` +
    `  src="${embedUrl}"\n` +
    `  width="100%"\n` +
    `  height="900"\n` +
    `  style="border: none;"\n` +
    `  title="Medicaid Qualification Assessment"\n` +
    `></iframe>`;

  const copy = (text: string, setFn: (v: boolean) => void) => {
    navigator.clipboard.writeText(text).then(() => {
      setFn(true);
      setTimeout(() => setFn(false), 2500);
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Share fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: "none",
          color: "#8200DC",
          borderColor: "#8200DC",
          "&:hover": {
            borderColor: "#8200DC",
            backgroundColor: "rgba(130, 0, 220, 0.04)",
          },
        }}
      >
        Share Assessment Link
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Medicaid Assessment Access
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this link directly with clients or embed the form on your
            website. Submissions are automatically linked to your firm.
          </Typography>

          <Box mb={2}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
            >
              <Tab label="Direct Link" />
              <Tab label="Embed Snippet" />
            </Tabs>
            <Divider />
          </Box>

          {tab === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="a"
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: "13px",
                  color: "#5b4fcf",
                  wordBreak: "break-all",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {embedUrl}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  copiedLink ? (
                    <CheckCircleOutline fontSize="small" />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )
                }
                onClick={() => copy(embedUrl, setCopiedLink)}
                sx={{
                  textTransform: "none",
                  flexShrink: 0,
                  color: copiedLink ? "#16a34a" : "#8200DC",
                  borderColor: copiedLink ? "#16a34a" : "#8200DC",
                  "&:hover": {
                    borderColor: copiedLink ? "#16a34a" : "#8200DC",
                    backgroundColor: copiedLink
                      ? "rgba(22, 163, 74, 0.04)"
                      : "rgba(130, 0, 220, 0.04)",
                  },
                }}
              >
                {copiedLink ? "Copied!" : "Copy Link"}
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Box
                component="pre"
                sx={{
                  mt: 0.5,
                  mb: 1.5,
                  p: 2,
                  backgroundColor: "#1e1e2e",
                  color: "#cdd6f4",
                  borderRadius: 1,
                  fontSize: "13px",
                  overflowX: "auto",
                  fontFamily: "monospace",
                  lineHeight: 1.6,
                  whiteSpace: "pre",
                }}
              >
                {iframeSnippet}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  copiedSnippet ? (
                    <CheckCircleOutline fontSize="small" />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )
                }
                onClick={() => copy(iframeSnippet, setCopiedSnippet)}
                sx={{
                  textTransform: "none",
                  color: copiedSnippet ? "#16a34a" : "#8200DC",
                  borderColor: copiedSnippet ? "#16a34a" : "#8200DC",
                  "&:hover": {
                    borderColor: copiedSnippet ? "#16a34a" : "#8200DC",
                    backgroundColor: copiedSnippet
                      ? "rgba(22, 163, 74, 0.04)"
                      : "rgba(130, 0, 220, 0.04)",
                  },
                }}
              >
                {copiedSnippet ? "Copied!" : "Copy Snippet"}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MedicaidEmbedInstructions;
